import { useCallback } from 'react';
import { CompanyType } from 'UI/constants/entityTypes';

import { DRAWER_TYPES } from '../components/DrawerStack/DrawerStack.constants';
import { SAVE_CONFIG } from '../components/DrawerStack/DrawerStack.utils';
import { useSendoutsDrawerStack } from '../context';

const isCompanySignedOrClient = company => {
  if (!company || !company.type) return false;
  return company.type?.id === CompanyType.Vendor || company.type?.id === CompanyType.Client;
};

export const useSendoutsDrawerHandlers = ({ jobOrder }) => {
  const { stack, openDrawer, closeDrawer, clearStack } = useSendoutsDrawerStack();

  const openCreateCall = useCallback(() => {
    openDrawer(DRAWER_TYPES.CreateCall, { jobOrder });
  }, [jobOrder, openDrawer]);

  const openCallList = useCallback(() => {
    openDrawer(DRAWER_TYPES.CallList, {
      onCreateCall: () => openCreateCall(null),
      onEditCall: callId => openCreateCall(callId)
    });
  }, [openCreateCall, openDrawer]);

  const openCreateSendout = useCallback(
    ({ accountableId, collaboratorId }) => {
      openDrawer(DRAWER_TYPES.CreateSendout, {
        jobOrderId: jobOrder?.id,
        accountableId,
        collaboratorId,
        company: {
          id: jobOrder?.company?.id,
          name: jobOrder?.company?.name,
          isSignedOrClient: isCompanySignedOrClient(jobOrder?.company)
        },
        onSaveAsDraft: () => {
          openDrawer(DRAWER_TYPES.Drafts, {
            jobOrderId: jobOrder?.id
          });
        },
        onViewDuplicates: ({ candidate }) => {
          openDrawer(DRAWER_TYPES.Duplicates, { jobOrder, candidate });
        }
      });
    },
    [jobOrder, openDrawer]
  );

  const openCreateSendover = useCallback(
    ({ accountableId }) => {
      openDrawer(DRAWER_TYPES.CreateSendover, {
        jobOrderId: jobOrder.id,
        accountableId,
        company: {
          id: jobOrder?.company?.id,
          name: jobOrder?.company?.name,
          isSignedOrClient: isCompanySignedOrClient(jobOrder?.company)
        }
      });
    },
    [openDrawer, jobOrder]
  );

  const openSendoutDetails = useCallback(
    ({ sendoutId }) => {
      openDrawer(DRAWER_TYPES.SendoutDetails, {
        sendoutId,
        onOpenCalls: openCallList
      });
    },
    [openCallList, openDrawer]
  );

  const openSendoverDetails = useCallback(
    ({ sendoverId }) => {
      openDrawer(DRAWER_TYPES.SendoverDetails, {
        sendoverId,
        onConvertToSendout: openCreateSendout
      });
    },
    [openCreateSendout, openDrawer]
  );

  const openDrafts = useCallback(() => {
    openDrawer(DRAWER_TYPES.Drafts, {
      onSelectDraft: draft =>
        openDrawer(
          draft?.type === 'sendover' ? DRAWER_TYPES.CreateSendover : DRAWER_TYPES.CreateSendout,
          {
            jobOrder,
            onOpenCalls: openCallList
          }
        )
    });
  }, [openCallList, openDrawer, jobOrder]);

  const onDrawerSaved = useCallback(
    async (meta, payload) => {
      const handlerConfig = SAVE_CONFIG[meta.type];
      if (handlerConfig?.handler) {
        const response = await handlerConfig.handler({ meta, payload });
        if (!response?.success) {
          return;
        }
      }

      closeDrawer(meta.key);

      if (payload?.closeAll) {
        clearStack();
      }
    },
    [clearStack, closeDrawer]
  );

  const onDrawerClose = useCallback(
    key => {
      closeDrawer(key);
    },
    [closeDrawer]
  );

  return {
    stack,
    openCreateSendout,
    openCreateSendover,
    openSendoutDetails,
    openSendoverDetails,
    openDrafts,
    openCallList,
    openCreateCall,
    onDrawerSaved,
    onDrawerClose
  };
};
